// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A data table component for displaying and managing asset information.
 * This module provides a configurable table view with sorting, filtering,
 * and responsive column display capabilities. The table automatically
 * adapts its layout based on screen size and user preferences.
 *
 * Key features:
 * - Responsive design with column visibility control
 * - Sortable columns with persistent sort state
 * - Customizable empty states for zero-data scenarios
 * - Optional row count indicator with export functionality
 * - Integration with remote API for data fetching
 * - Support for filtering by various asset properties
 */
import { AssetFilter, AssetSortProperty } from '@/asset/api/get-assets';
import { AssetTableRow } from '@/asset/components/asset-table-row';
import { useAssets } from '@/asset/hooks/use-assets';
import { EmptyState } from '@/components/empty-state';
import { TablePaginationFooter } from '@/components/table-pagination-footer';
import {
  ASSET_TABLE_COLUMNS,
  AssetTableColumn,
} from '@/constants/asset-table-column';
import { ArrowOutwardRounded } from '@/constants/hardfin-standard-icons';
import { Sort } from '@/constants/sort';
import { useTableSort } from '@/hooks/use-table-sort';
import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { ReactNode, useEffect } from 'react';

/**
 * Props for configuring the AssetTable component.
 * @typedef {Object} Props
 */
interface Props {
  /**
   * The current sort state of the table.
   * Defines which column to sort by and in what direction.
   * Example: { property: 'serial', direction: 'asc' }
   */
  sort: Sort<AssetSortProperty>;

  /**
   * Optional filter to apply to the assets requested from the remote API.
   * Can filter by ownership, location, status, and other criteria.
   * Example: { owner: 'customer-123', status: 'active' }
   */
  filter?: AssetFilter;

  /**
   * Optional array of columns to display in the table.
   * If not provided, all columns will be displayed.
   * Supports responsive behavior with some columns hidden on mobile.
   * Example: ['serial', 'location', 'owner']
   */
  columns?: AssetTableColumn[];

  /**
   * Layout for recent activity entries.
   */
  activityLayout?: 'inline' | 'stacked';

  /**
   * Optional component to render when the table has no data.
   * If not provided, a default empty state is shown with guidance.
   * Example: <CustomEmptyState message="No matching assets found" />
   */
  emptyState?: ReactNode;

  /**
   * A boolean flag that indicates whether a download indicator should be displayed.
   * If set to true, the download indicator is shown; if false or undefined, it is hidden.
   */
  showDownloadIndicator?: boolean;

  /**
   * Callback fired when a sort header is clicked.
   * Receives the column property to sort by.
   * Used to maintain sort state in parent components.
   *
   * @param property - The column property to sort by (e.g. 'serial', 'owner')
   */
  onSort: (property: AssetSortProperty) => void;

  onPageChange?: (page: number) => void;
}

/**
 * A table component for displaying asset information with sorting and filtering.
 * Fetches asset data from the remote API and displays it in a configurable table
 * format. Supports sorting by various columns, responsive column visibility, and
 * custom empty states.
 *
 * Features:
 * - Sortable columns for serial, product, project, location, owner, and activity
 * - Responsive design with some columns hidden on mobile devices
 * - Configurable column visibility through props
 * - Custom empty state support for zero-data scenarios
 * - Optional row count indicator with export functionality
 * - Integration with useAssets hook for data fetching
 * - Support for filtering through AssetFilter prop
 *
 * Usage example:
 * ```tsx
 * <AssetTable
 *   sort={{ property: 'serial', direction: 'asc' }}
 *   filter={{ status: 'active' }}
 *   columns={['serial', 'location', 'owner']}
 *   showCountIndicator={true}
 *   onSort={handleSort}
 * />
 * ```
 *
 * @param props - Configuration options for the table
 * @throws Will throw a promise during initial data fetch (for Suspense)
 */
export function AssetTable(props: Props) {
  const { assets, totalCount: totalAssetCount } = useAssets({
    filter: props.filter,
  });
  const { getSortDirection, getLabelDirection, isLabelActive } = useTableSort(
    props.sort
  );

  // Determine which columns to display based on props or defaults
  const columns = props.columns ?? ASSET_TABLE_COLUMNS;
  const showSerial = columns.includes('serial');
  const showProject = columns.includes('project');
  const showProduct = columns.includes('product');
  const showLocation = columns.includes('location');
  const showOwner = columns.includes('owner');
  const showZone = columns.includes('zone');
  const showActivity = columns.includes('activity');
  const downloadURL = new URL(
    '/v1/export/assets',
    import.meta.env.VITE_API_URL
  );

  // Create sort handler for column headers
  const createSortHandler = (column: AssetSortProperty) => () => {
    props.onSort(column);
  };
  const pageNumber = props.filter?.pageNumber ?? 1;
  const pageSize = props.filter?.pageSize ?? 50;
  const onPageChange = props.onPageChange;
  const pagination = onPageChange
    ? {
        page: pageNumber,
        pageSize,
        onPageChange,
      }
    : undefined;

  // Handle page overrun
  useEffect(() => {
    if (onPageChange && assets.length === 0 && pageNumber > 1) {
      onPageChange(1);
    }
  }, [assets.length, pageNumber, onPageChange]);

  // Show empty state if no assets found
  if (assets.length === 0) {
    if (props.emptyState === undefined) {
      return (
        <EmptyState title="No assets found">
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography color="text.secondary">
              To start tracking assets in Hardfin, add a new asset
            </Typography>
            <ArrowOutwardRounded fontSize="small" color="action" />
          </Stack>
        </EmptyState>
      );
    }

    return props.emptyState;
  }

  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              {showSerial && (
                <TableCell sortDirection={getSortDirection('serial')}>
                  <TableSortLabel
                    active={isLabelActive('serial')}
                    direction={getLabelDirection('serial')}
                    onClick={createSortHandler('serial')}
                  >
                    Serial
                  </TableSortLabel>
                </TableCell>
              )}
              {showProduct && (
                <TableCell
                  sortDirection={getSortDirection('item')}
                  sx={{ display: { xs: 'none', sm: 'table-cell' } }}
                >
                  <TableSortLabel
                    active={isLabelActive('item')}
                    direction={getLabelDirection('item')}
                    onClick={createSortHandler('item')}
                  >
                    Product
                  </TableSortLabel>
                </TableCell>
              )}
              {showProject && (
                <TableCell
                  sortDirection={getSortDirection('project')}
                  sx={{ display: { xs: 'none', sm: 'table-cell' } }}
                >
                  <TableSortLabel
                    active={isLabelActive('project')}
                    direction={getLabelDirection('project')}
                    onClick={createSortHandler('project')}
                  >
                    Project
                  </TableSortLabel>
                </TableCell>
              )}
              {showLocation && (
                <TableCell sortDirection={getSortDirection('location')}>
                  <TableSortLabel
                    active={isLabelActive('location')}
                    direction={getLabelDirection('location')}
                    onClick={createSortHandler('location')}
                  >
                    Location
                  </TableSortLabel>
                </TableCell>
              )}
              {showZone && (
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  Zone
                </TableCell>
              )}
              {showOwner && (
                <TableCell
                  sortDirection={getSortDirection('owner')}
                  sx={{ display: { xs: 'none', sm: 'table-cell' } }}
                >
                  <TableSortLabel
                    active={isLabelActive('owner')}
                    direction={getLabelDirection('owner')}
                    onClick={createSortHandler('owner')}
                  >
                    Owner
                  </TableSortLabel>
                </TableCell>
              )}
              {showActivity && (
                <TableCell
                  sx={{
                    display: { xs: 'none', sm: 'table-cell' },
                    minWidth: '20%',
                    width: '20%',
                  }}
                >
                  Recent activity
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {assets.map((asset) => (
              <AssetTableRow
                key={asset.id}
                asset={asset}
                columns={columns}
                activityLayout={props.activityLayout}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePaginationFooter
        pagination={pagination}
        totalCount={totalAssetCount}
        entityOptions={{ singular: 'asset', includeCount: false }}
        downloadURL={props.showDownloadIndicator ? downloadURL : undefined}
        downloadLabel="Export asset data"
      />
    </>
  );
}
